// react
import { ChangeEvent, Dispatch, FormEvent, SetStateAction, useContext, useState } from 'react';

// types
import { ErrorCause, Feedback, IfrPlanPayload } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';

// props
export interface FormEditIfrPlanProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormEditIfrPlan = ({ onClose, onSuccess, setIsLoading }: FormEditIfrPlanProps) => {
	// hooks
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [values, setValues] = useState({
		name: '',
	});

	// params
	const paramIfrPlan = searchParams.get('dialogIfrPlan');

	// context
	const { setBanner } = useContext(ContextBanner);

	// vars
	const idForm = 'form-edit-ifr-plan';

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch IFR plan
	useFetch({
		isEnabled: Boolean(paramIfrPlan),
		url: `${process.env.REACT_APP_API_URL}/ifrs/plans/${paramIfrPlan}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setValues({
					name: res.data.name || '',
				});
			}
		},
	});

	// handlers
	const handleChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
		setValues({ ...values, [e.target.name]: e.target.value });
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear feedback
			setFeedback(null);

			// set loading
			setIsLoading(true);

			if (!paramIfrPlan) {
				throw new Error('An ID is required', {
					cause: {
						id: 'ID_NON_EXISTENT',
					},
				});
			}

			const payload: Partial<IfrPlanPayload> = {
				name: values.name,
			};

			const fetchEditIfrPlan = await fetch(
				`${process.env.REACT_APP_API_URL}/ifrs/plans/${paramIfrPlan}`,
				{
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify(payload),
				}
			);
			const resEditIfrPlan = await fetchEditIfrPlan.json();

			if (resEditIfrPlan.error) {
				throw new Error(resEditIfrPlan.error.message, {
					cause: {
						id: resEditIfrPlan.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.ifrPlanUpdated', { name: entries.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'IFR_PLAN_UNAUTHORIZED_PATCH') {
					feedbackNew.alert.children = t('error.unauthorized');
				} else if (cause.id === 'IFR_PLAN_DUPLICATE') {
					feedbackNew.alert.children = t('error.ifrPlanDuplicate');
				} else if (cause.id === 'IFR_PLAN_NOT_FOUND') {
					feedbackNew.alert.children = t('error.ifrPlanNotFound');
				}
			}

			setFeedback(feedbackNew);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<form id={idForm} onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required
						value={values.name}
					/>
				</Grid>
			</Grid>
			{feedback?.alert && (
				<Grid item xs={12}>
					<Alert severity="error" {...feedback.alert}>
						{feedback.title && <AlertTitle {...feedback.title} />}
						{feedback.alert.children}
					</Alert>
				</Grid>
			)}
		</form>
	);
};
